import { Link } from "react-router-dom";
import { ArrowRight, FileCheck, Building2, CalendarClock } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Motion";
import EditorialSources from "@/components/EditorialSources";
import PageSeo from "@/components/PageSeo";
import { hy } from "@/lib/hyphenate";

const STEPS = [
  {
    n: "01",
    title: "Vous demandez un devis",
    text: "Remplissez le formulaire en 2 minutes : activité, chiffre d'affaires, effectif, coordonnées.",
  },
  {
    n: "02",
    title: "Vous souscrivez",
    text: "Après étude de votre dossier, une proposition peut être présentée. Vérifiez les activités, les garanties et la date de prise d’effet avant de signer.",
  },
  {
    n: "03",
    title: "Vous recevez votre attestation",
    text: "L’attestation est délivrée après acceptation du dossier et selon les conditions de prise d’effet du contrat. Avant le chantier, vérifiez qu’elle correspond aux travaux, à la zone et à la date d’ouverture prévus.",
  },
];

export default function Attestation() {
  return (
    <div data-testid="attestation-page">
      <PageSeo path="/attestation" />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-24">
        <Reveal y={12}>
          <SectionTag className="mb-4">Attestation décennale</SectionTag>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.9] mb-6">
            {hy("Attestation décennale")} :<br /><span className="text-accent">{hy("comment l’obtenir ?")}</span>
          </h1>
          <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-ink/75 mb-20">
            L'attestation d'assurance décennale est le document qui prouve à vos clients,
            maîtres d'ouvrage et partenaires que votre entreprise est couverte pour les travaux
            concernés. Elle doit être jointe à vos devis et factures : sans elle, impossible
            d'ouvrir un chantier sereinement.
          </p>
        </Reveal>

        <div className="grid gap-12 lg:grid-cols-2 mb-20">
          <Reveal>
            <div className="border-2 border-ink bg-white p-8 sm:p-10 shadow-brutal h-full">
              <FileCheck className="h-8 w-8 text-accent mb-6" />
              <h2 className="font-display text-2xl font-bold uppercase tracking-tighter mb-4">
                Ce que contient l'attestation
              </h2>
              <ul className="space-y-3 text-sm leading-relaxed text-ink/75">
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>L'identité de l'assureur et le numéro de contrat</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>La période de validité de la garantie</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>Les activités couvertes (elles doivent correspondre exactement à vos travaux)</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>La zone géographique de couverture</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>Les garanties souscrites : RC Pro et/ou décennale</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-2 border-ink bg-night text-paper p-8 sm:p-10 shadow-brutal-accent h-full">
              <Building2 className="h-8 w-8 text-accent mb-6" />
              <h2 className="font-display text-2xl font-bold uppercase tracking-tighter mb-4">
                Quand la présenter ?
              </h2>
              <ul className="space-y-3 text-sm leading-relaxed text-paper/75">
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span><span>À joindre aux devis et factures des professionnels soumis à l’obligation d’assurance décennale (article L243-2 du Code des assurances) — <Link to="/guide/mention-decennale-devis-factures" className="underline underline-offset-4 hover:text-accent">règles et modèle d’encart</Link></span></li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>Lors de la signature d'un devis ou d'un marché de travaux</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>Aux donneurs d'ordre si vous intervenez en sous-traitance</li>
                <li className="flex gap-3"><span className="font-mono text-[10px] text-accent pt-1">—</span>Aux banques et partenaires qui peuvent l'exiger</li>
              </ul>
              <p className="mt-6 border-t border-paper/15 pt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
                Contrôlez la couverture avant l’ouverture du chantier
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal className="mb-4">
          <SectionTag>Comment l'obtenir</SectionTag>
        </Reveal>
        <div className="mb-20">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="grid gap-4 border-t-2 border-ink py-8 sm:grid-cols-12 sm:gap-8 items-start">
                <p className="font-display text-5xl font-extrabold tracking-tighter text-accent sm:col-span-2">{s.n}</p>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tighter sm:col-span-4">{s.title}</h3>
                <p className="text-base leading-relaxed text-ink/75 sm:col-span-6">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="flex flex-wrap items-center justify-between gap-6 border-2 border-ink bg-accent text-paper p-8 sm:p-12">
          <div className="flex items-start gap-4">
            <CalendarClock className="h-8 w-8 shrink-0" />
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter">
                Besoin d'une attestation rapidement ?
              </h2>
              <p className="mt-2 text-sm text-paper max-w-xl">
                Signalez la date de votre prochain chantier dans votre demande.
                Le délai de délivrance dépend de l’étude du dossier et des conditions de l’assureur.
              </p>
            </div>
          </div>
          <Link
            to="/devis"
            data-testid="attestation-cta-devis"
            className="inline-flex items-center gap-2 border-2 border-night bg-night px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="my-10 border-2 border-ink bg-white p-6">
          <h2 className="font-display text-2xl font-bold mb-3">Vous avez déjà une attestation ?</h2>
          <p className="leading-relaxed text-ink/75 mb-3">Identité de l’entreprise, activités, techniques et dates : contrôlez sa portée pour le chantier concerné.</p>
          <Link to="/guide/verifier-attestation-decennale" className="underline underline-offset-4 font-bold hover:text-accent">Les points à vérifier sur une attestation décennale</Link>
        </div>
        <EditorialSources updated="2026-09-24" />
      </div>
    </div>
  );
}
